import React, { useEffect, useState, lazy, Suspense } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import ExecutionEnvironment from "exenv";

// Components
import CustomMeta from "../components/CustomMeta/CustomMeta";

const FoundingPartnerEmailVerification = lazy(() =>
  import(
    /* webpackChunkName: "FoundingPartnerEmailVerification" */ "../components/FoundingPartners/FoundingPartnerEmailVerification"
  )
);
const LoginOrSignup = lazy(() =>
  import(
    /* webpackChunkName: "LoginOrSignup" */ "../components/Login/LoginOrSignup"
  )
);
const SetFoundingPartnerAffiliateID = lazy(() =>
  import(
    /* webpackChunkName: "SetFoundingPartnerAffiliateID" */ "../components/FoundingPartners/SetFoundingPartnerAffiliateID"
  )
);

import { makeStyles } from "tss-react/mui";
import { Typography, CircularProgress, Alert, Button } from "@mui/material";

// redux stuff
import { connect } from "react-redux";
import {
  checkFoundingPartnerUser,
  upgradeToFoundingPartner,
  validateFoundingPartnerInvite,
} from "../redux/actions/dataActions";

const useStyles = makeStyles()((theme) => ({
  fullPageHeight: {
    minHeight: "100vh",
  },
  heading: {
    paddingTop: theme.spacing(2),
  },
  subTitle: {
    padding: "0px 30px 20px 30px",
    textAlign: "center",
    fontSize: 20,
  },
  loadingDiv: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  gridContainer: {
    display: "flex",
    justifyContent: "center",
    padding: `0 ${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)}`,
  },
  alertContainer: {
    maxWidth: 600,
    margin: "0 auto",
    marginBottom: theme.spacing(2),
  },
  logoutButton: {
    marginTop: theme.spacing(2),
  },
}));

function FoundingPartnersAcceptInvite(props) {
  const { classes } = useStyles();
  const history = useHistory();

  const [step, setStep] = useState("validate_email"); // validate_email, check_auth, register_affiliate
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDetails, setInviteDetails] = useState(null);
  const [authStatus, setAuthStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Check if user is returning from signup/login
    const foundingPartnerInProgress = localStorage.getItem(
      "foundingPartnerInProgress"
    );
    const storedInviteEmail = localStorage.getItem(
      "foundingPartnerInviteEmail"
    );

    if (foundingPartnerInProgress === "true" && storedInviteEmail) {
      // Validate the stored email and continue flow
      props
        .validateFoundingPartnerInvite(storedInviteEmail)
        .then((result) => {
          setInviteEmail(storedInviteEmail);
          setInviteDetails(result.invite);

          // Check user status and continue flow
          if (props.user.authenticated) {
            checkUserStatus(storedInviteEmail);
          }
        })
        .catch((err) => {
          console.error("Error resuming founding partner flow:", err);
          // Clear localStorage if validation fails
          localStorage.removeItem("foundingPartnerInProgress");
          localStorage.removeItem("foundingPartnerInviteEmail");
        });
    }

    // Check if user is already authenticated and has founding partner profile
    if (props.user.authenticated && props.user.credentials.email) {
      // Check if they already have a founding partner affiliate profile
      // This would redirect them if they're already set up
    }
  }, [props.user.authenticated]);

  function handleEmailValidated(email, details) {
    setInviteEmail(email);
    setInviteDetails(details);
    setStep("check_auth");

    // Store founding partner context in localStorage for after signup/login
    localStorage.setItem("foundingPartnerInviteEmail", email);
    localStorage.setItem("foundingPartnerInProgress", "true");

    // Give user a moment to see success message
    setTimeout(() => {
      checkUserStatus(email);
    }, 2000);
  }

  function checkUserStatus(email) {
    props
      .checkFoundingPartnerUser(email)
      .then((result) => {
        setAuthStatus(result);

        if (!result.authenticated) {
          // User needs to log in or sign up
          if (result.userExists) {
            setStep("login");
          } else {
            setStep("signup");
          }
        } else if (!result.emailMatches) {
          // Wrong account
          setStep("wrong_email");
          setErrorMessage(result.message);
        } else if (result.hasAffiliateProfile) {
          // Has existing affiliate profile - upgrade it
          setStep("upgrading");
          upgradeExistingAffiliate(email);
        } else {
          // Ready to register
          setStep("register_affiliate");
        }
      })
      .catch((err) => {
        console.error("Error checking user status:", err);
        setErrorMessage("Error checking account status. Please try again.");
      });
  }

  function handleAuthComplete() {
    // After user logs in or signs up, check status again
    checkUserStatus(inviteEmail);
  }

  function upgradeExistingAffiliate(email) {
    props
      .upgradeToFoundingPartner(email)
      .then(() => {
        // Clear founding partner localStorage
        localStorage.removeItem("foundingPartnerInProgress");
        localStorage.removeItem("foundingPartnerInviteEmail");

        // Upgrade successful, redirect to dashboard
        setTimeout(() => {
          history.push("/founding-partner-dashboard");
        }, 2000);
      })
      .catch((err) => {
        console.error("Error upgrading affiliate:", err);
        setStep("upgrade_error");
        setErrorMessage(
          err?.error || "Error upgrading your account. Please contact support."
        );
      });
  }

  function getContent() {
    if (props.userLoading && step !== "validate_email") {
      return (
        <div className={classes.loadingDiv}>
          <CircularProgress size={40} />
          <Typography>Loading...</Typography>
        </div>
      );
    }

    switch (step) {
      case "validate_email":
        return (
          <FoundingPartnerEmailVerification
            onValidated={handleEmailValidated}
          />
        );

      case "check_auth":
        return (
          <div className={classes.loadingDiv}>
            <CircularProgress size={40} />
            <Typography>Checking account status...</Typography>
          </div>
        );

      case "upgrading":
        return (
          <div className={classes.loadingDiv}>
            <CircularProgress size={40} />
            <Typography variant="h3" gutterBottom>
              Upgrading Your Account
            </Typography>
            <Typography>
              We're upgrading your existing affiliate account to a founding
              partner with 40% lifetime commission...
            </Typography>
          </div>
        );

      case "upgrade_error":
        return (
          <div className={classes.alertContainer}>
            <Alert severity="error">
              {errorMessage}
              <Button
                className={classes.logoutButton}
                variant="outlined"
                onClick={() => {
                  window.location.href = "/contact";
                }}
              >
                Contact Support
              </Button>
            </Alert>
          </div>
        );

      case "login":
        // Set redirect URL for after login
        localStorage.setItem(
          "redirectAfterLogin",
          "/founding-partners-accept-invite"
        );

        return (
          <div>
            <div className={classes.alertContainer}>
              <Alert severity="info">
                We found an existing account with your email address. Please log
                in to continue with your founding partner registration.
              </Alert>
            </div>
            <LoginOrSignup
              titleText="Log in to Continue Your Founding Partner Registration"
              defaultEmail={inviteEmail}
              onSuccess={handleAuthComplete}
            />
          </div>
        );

      case "signup":
        // Set redirect URL for after signup
        localStorage.setItem(
          "redirectAfterLogin",
          "/founding-partners-accept-invite"
        );

        return (
          <div>
            <div className={classes.alertContainer}>
              <Alert severity="success">
                Great! Let's create your Content Generator account to get
                started.
              </Alert>
            </div>
            <LoginOrSignup
              titleText="Create Your Account to Complete Founding Partner Registration"
              defaultEmail={inviteEmail}
              onSuccess={handleAuthComplete}
            />
          </div>
        );

      case "wrong_email":
        return (
          <div className={classes.alertContainer}>
            <Alert severity="error">
              {errorMessage}
              <Button
                className={classes.logoutButton}
                variant="outlined"
                onClick={() => {
                  // Implement logout and redirect to login
                  window.location.href = "/logout";
                }}
              >
                Log Out and Try Again
              </Button>
            </Alert>
          </div>
        );

      case "register_affiliate":
        return <SetFoundingPartnerAffiliateID inviteEmail={inviteEmail} />;

      default:
        return (
          <div className={classes.loadingDiv}>
            <Typography>Loading...</Typography>
          </div>
        );
    }
  }

  return (
    <div className={classes.fullPageHeight}>
      <CustomMeta
        title="Accept Your Founding Partner Invitation - Content Generator"
        description="Complete your founding partner registration with exclusive 40% lifetime commissions, free lifetime access, and exclusive benefits."
        url="https://contentgenerator.io/founding-partners-accept-invite"
      />
      <Typography variant="h1" className={classes.heading}>
        Founding Partner Invitation
      </Typography>
      <Typography className={classes.subTitle}>
        Accept your exclusive invitation to shape the future of Content
        Generator
      </Typography>
      <div className={classes.gridContainer}>
        {ExecutionEnvironment.canUseDOM && (
          <Suspense
            fallback={
              <div className={classes.loadingDiv}>
                <CircularProgress size={40} />
              </div>
            }
          >
            {getContent()}
          </Suspense>
        )}
      </div>
    </div>
  );
}

FoundingPartnersAcceptInvite.propTypes = {
  user: PropTypes.object.isRequired,
  userLoading: PropTypes.bool.isRequired,
  checkFoundingPartnerUser: PropTypes.func.isRequired,
  upgradeToFoundingPartner: PropTypes.func.isRequired,
  validateFoundingPartnerInvite: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
  userLoading: state.user.loading,
});

export default connect(mapStateToProps, {
  checkFoundingPartnerUser,
  upgradeToFoundingPartner,
  validateFoundingPartnerInvite,
})(FoundingPartnersAcceptInvite);
